import { Button } from "@/components/ui/button";
import { num, rupee } from "@/lib/api";
import { PayoffToggle } from "@/components/PayoffChart";

const Row = ({ k, v, cls = "text-slate-100", id }) => (
  <div className="flex items-baseline justify-between border-b border-slate-800/60 py-1.5 text-xs">
    <span className="text-slate-500">{k}</span>
    <span data-testid={id} className={`num ${cls}`}>{v}</span>
  </div>
);

export const SpreadCard = ({ sug, onTake, busy, forceDir, onForce, risk }) => {
  const has = sug && sug.direction && sug.direction !== "NO_TRADE" && !sug.error;
  return (
    <div className="panel p-4" data-testid="card-debit-spread-suggestion">
      <div className="flex items-center justify-between">
        <span className="eyebrow">Suggested debit spread · max risk {rupee(risk)}</span>
      </div>
      <div className="mt-2 flex gap-1">
        {["AUTO", "LONG", "SHORT"].map((d) => (
          <button
            key={d}
            data-testid={`spread-dir-${d.toLowerCase()}`}
            onClick={() => onForce(d === "AUTO" ? null : d)}
            className={`rounded px-2 py-1 text-[10px] font-semibold tracking-wider transition-colors ${
              (forceDir || "AUTO") === d ? "bg-slate-700 text-slate-50" : "text-slate-500 hover:text-slate-300"
            }`}
          >
            {d === "AUTO" ? "FROM SIGNAL" : d}
          </button>
        ))}
      </div>
      {!has ? (
        <p className="mt-4 text-sm text-slate-500" data-testid="spread-empty">
          {sug?.error || "No active signal. Choose LONG or SHORT above to preview a spread."}
        </p>
      ) : (
        <div className="mt-3 fade-in">
          <div className={`font-display text-lg font-semibold ${sug.direction === "LONG" ? "up" : "down"}`} data-testid="spread-strategy">
            {sug.strategy}
          </div>
          <div className="num text-xs text-slate-500">exp {sug.expiry} · {sug.lots} lot × {sug.lot_size}</div>
          <div className="mt-3">
            <Row k={`Buy ${num(sug.buy_strike, 0)} ${sug.opt_type}`} v={num(sug.buy_price)} id="spread-buy-leg" />
            <Row k={`Sell ${num(sug.sell_strike, 0)} ${sug.opt_type}`} v={num(sug.sell_price)} id="spread-sell-leg" />
            <Row k="Net debit (pts)" v={num(sug.debit)} id="spread-debit" />
            <Row k="Stop / Target spread value" v={`${num(sug.stop_value)} / ${num(sug.target_value)}`} id="spread-stop-target" />
            <Row k="Planned risk" v={rupee(sug.planned_risk)} cls="down" id="spread-planned-risk" />
            <Row k="Target profit" v={rupee(sug.target_profit)} cls="up" id="spread-target-profit" />
            <Row k="Max theoretical loss" v={rupee(sug.max_theoretical_loss)} cls="text-slate-400" id="spread-max-loss" />
            <Row k="Breakeven" v={num(sug.breakeven)} id="spread-breakeven" />
          </div>
          {sug.stop_uses_exit && (
            <p className="mt-2 text-[11px] leading-snug text-amber-400/90">
              Full debit exceeds your risk cap, so risk is held to {rupee(sug.planned_risk)} by a stop on spread value. A gap past the stop can lose more.
            </p>
          )}
          <PayoffToggle trade={sug} spot={sug.spot} id="payoff-spread-suggestion" />
          <Button data-testid="button-execute-paper-trade" disabled={busy} onClick={onTake} className="mt-4 w-full bg-indigo-500 text-white hover:bg-indigo-400">
            {busy ? "Placing…" : "Take as paper trade"}
          </Button>
        </div>
      )}
    </div>
  );
};
